/**
 * featureAccess — package entitlements for feature enforcement.
 * Maps app routes → feature keys and provides a cached entitlements hook.
 * Fail-open: if the entitlements API errors, features stay accessible.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const HEALTH_KINDS = [
    "medical-history", "chronic-disease", "cancer-history", "genetic-condition",
    "allergy-registry", "vaccination", "lifestyle", "pregnancy-birth",
    "organ-donation", "blood-group",
];
const CULTURE_KINDS = ["recipe", "family-tradition", "family-ritual", "dialect", "heritage-quiz"];

const ROUTE_FEATURES = [
    ["/family-tree", "family_tree"],
    ["/add-member", "family_tree"],
    ["/invite", "invite_family"],
    ["/family-world-map", "world_map"],
    ["/memories", "photo_memories"],
    ["/record-story", "audio_memories"],
    ["/capsule", "video_memories"],
    ["/calendar", "family_calendar"],
    ["/celebrations", "family_calendar"],
    ["/timeline", "family_timeline"],
    ["/reminders", "smart_reminders"],
    ["/health-repository", "health_repository"],
    ["/vault", "document_vault"],
    ["/legal-vault", "legal_vault"],
    ["/poster", "digital_tree_poster"],
    ["/downloads", "download_export"],
    ["/ai/family-advisor", "ai_family_advisor"],
    ["/ai/family-story", "ai_story_generator"],
    ["/marriages", "marriages_records"],
    ["/achievements", "achievements"],
];

export function featureForPath(pathname) {
    if (pathname.startsWith("/family/records/")) {
        const kind = pathname.split("/")[3] || "";
        if (HEALTH_KINDS.includes(kind)) return "health_repository";
        if (CULTURE_KINDS.includes(kind)) return "culture_rituals";
        return null;
    }
    for (const [p, f] of ROUTE_FEATURES) {
        if (pathname === p || pathname.startsWith(`${p}/`)) return f;
    }
    return null;
}

let _cache = null;
let _at = 0;
let _inflight = null;
const TTL = 30000;

export async function getEntitlements(force = false) {
    if (!force && _cache && Date.now() - _at < TTL) return _cache;
    if (_inflight) return _inflight;
    _inflight = api.get("/packages/me")
        .then(({ data }) => {
            _cache = data;
            _at = Date.now();
            return data;
        })
        .catch(() => {
            _cache = { features: null, error: true };
            _at = Date.now();
            return _cache;
        })
        .finally(() => { _inflight = null; });
    return _inflight;
}

export const clearEntitlements = () => { _cache = null; _at = 0; };

export function useEntitlements() {
    const [ent, setEnt] = useState(_cache);
    useEffect(() => {
        let mounted = true;
        getEntitlements().then((d) => { if (mounted) setEnt(d); });
        return () => { mounted = false; };
    }, []);
    return ent;
}
